import { FC } from "react";
import {
  Controller,
  Control,
  UseFormRegister,
  UseFormSetValue,
  UseFormSetError,
  UseFormClearErrors,
  UseFormGetValues,
} from "react-hook-form";
import FormGroup from "app/components/common/inputs/FormGroup";
import CustomDatePicker from "app/components/common/inputs/CustomDatePicker";
import ErrorsMessage from "app/components/common/errors/ErrorsMessage";
import RadioButton from "app/components/common/buttons/RadioButton";
import InputField from "app/components/common/inputs/InputField";
import AutocompleteLocations from "app/components/common/AutocompleteLocations";
import SelectField from "app/components/common/inputs/SelectField";

import { stateOfIssueOptions } from "app/utils/staticValues";

interface Props {
  control: Control<any>;
  errors: any;
  register: UseFormRegister<any>;
  setValue: UseFormSetValue<any>;
  setError: UseFormSetError<any>;
  clearErrors: UseFormClearErrors<any>;
  getValues: UseFormGetValues<any>;

  jobTitle: string;
  employerName: string;
  startDate: string;
  endDate: string;
  startDateValue: string;
  secondjob: string;

  addressStreetName: string;
  addressStateName: string;
  addressPostCodeName: string;
}

const DynamicPreviousEmployer: FC<Props> = ({
  control,
  errors,
  register,
  setValue,
  setError,
  clearErrors,
  getValues,

  jobTitle,
  employerName,
  startDate,
  endDate,
  startDateValue,
  secondjob,

  addressStreetName,
  addressStateName,
  addressPostCodeName,
}) => {
  const getErrorMessage = (fieldName: string) => {
    const fieldNames = fieldName.split(".");
    return fieldNames.reduce((acc, key) => acc && acc[key], errors);
  };

  return (
    <>
      <FormGroup className="!mb-1">
        <InputField
          label="Job title"
          name={jobTitle}
          control={control}
          type="text"
          required={true}
          placeholder="Designer"
          error={getErrorMessage(jobTitle)?.message as string}
          className="flex-1"
          rules={{
            required: "Job title is required",
            pattern: {
              value: /^[A-Za-z\s]+$/,
              message: "Only alphabets are allowed",
            },
          }}
        />
        <InputField
          label="Previous employer name"
          name={employerName}
          control={control}
          type="text"
          required={true}
          placeholder="Katrina"
          error={getErrorMessage(employerName)?.message as string}
          className="flex-1"
          rules={{
            required: "Employer name is required",
            pattern: {
              value: /^[A-Za-z\s]+$/,
              message: "Only alphabets are allowed",
            },
          }}
        />
      </FormGroup>
      <FormGroup>
        <CustomDatePicker
          name={startDate}
          control={control}
          label="Start Date"
          placeholder="DD/MM/YYYY"
          rules={{ required: "Date is required" }}
          className="flex-1"
          isPastDate={true}
        />
        <CustomDatePicker
          name={endDate}
          control={control}
          label="End date"
          placeholder="DD/MM/YYYY"
          className="flex-1"
          isPastDate={true}
          minDate={startDateValue ? new Date(startDateValue) : undefined}
          rules={{
            required: "Date is required",
            validate: (value: Date | string | null) => {
              const startDate = getValues("startDate");
              if (!startDate || !value) return true;

              const start = new Date(startDate);
              const end = new Date(value);
              if (end < start) {
                return "End date cannot be before start date";
              }

              return true; // No errors
            },
          }}
        />
      </FormGroup>
      <FormGroup>
        <AutocompleteLocations
          label="Employer Address"
          FieldName={addressStreetName}
          placeholder="2464 Royal Ln. Mesa, New Jersey 45463"
          register={register}
          setValue={setValue}
          setError={setError}
          required={true}
          className="flex-1"
          error={getErrorMessage(addressStreetName)?.message as string}
          stateFieldName={addressStateName}
          postCodeFieldName={addressPostCodeName}
          clearErrors={clearErrors}
        />
        <SelectField
          label="state"
          name={addressStateName}
          control={control}
          options={stateOfIssueOptions}
          placeholder="Select state"
          error={getErrorMessage(addressStateName)?.message as string}
          required={true}
          className="flex-1"
        />
      </FormGroup>
      <FormGroup>
        <InputField
          label="Post Code"
          name={addressPostCodeName}
          control={control}
          type="number"
          required={true}
          placeholder="8767"
          error={getErrorMessage(addressPostCodeName)?.message as string}
          className="flex-1"
          rules={{
            min: {
              value: 1,
              message: "value must be greater than 0",
            },
          }}
        />
        <div className="text-left flex-1 ">
          <h6 className="font-Mulish text-nowrap text-base text-[#585C7B] mb-4">
            Do you have a second job?
          </h6>
          <div className="flex gap-5">
            <Controller
              name={secondjob}
              control={control}
              rules={{ required: "Please select an option" }}
              render={({ field: { value, onChange } }) => (
                <div className="flex gap-5">
                  <RadioButton
                    id={`yes-${secondjob}`}
                    name={secondjob}
                    value="yes"
                    label="Yes"
                    checked={value === "yes"}
                    onChange={onChange}
                  />
                  <RadioButton
                    id={`no-${secondjob}`}
                    name={secondjob}
                    value="no"
                    label="No"
                    checked={value === "no"}
                    onChange={onChange}
                  />
                </div>
              )}
            />
          </div>

          <ErrorsMessage
            title={getErrorMessage(secondjob)?.message as string}
            className="!text-left"
          />
        </div>
      </FormGroup>
    </>
  );
};

export default DynamicPreviousEmployer;
